import { useCallback, useEffect, useRef, useState } from "react";
import { getEnrolled, ENROLLED_PAGE_SIZE } from "../../../services/SsClassPublicService";
import { SsEnrolledSimulation } from "../../../types/self-serve.public.types";
import {
  clearEnrolmentToken,
  getValidEnrolmentSession,
  onEnrolmentDisplaced,
} from "../../../utils/self.serve.enrolment-storage";

/** Scroll container of the Self-Serve pages (see `.ss-class-public` in class.public.css). */
const SCROLL_ROOT_SELECTOR = ".ss-class-public";

/** Arguments for {@link useEnrolledSimulations}. */
interface UseEnrolledSimulationsArgs {
  /** Landing slug from the route. */
  slug?: string;
  /** Called when there is no usable session (missing, expired, revoked, or displaced). */
  onSessionExpired: () => void;
  /** Called with the API message when loading a page fails for another reason (e.g. 5xx, offline). */
  onError: (message: string) => void;
}

/**
 * Scroll pagination over the backend's paged `/enrolled` list (`page_number` / `page_size`).
 * Page 1 loads on mount; each later page is requested only when the sentinel element (placed
 * after the list) scrolls into view, and its simulations are appended. One request per page.
 * A missing, expired, or displaced enrolment session sends the student back to the landing page;
 * other load failures are reported through `onError` for the page's alert box.
 *
 * @returns `loading` (first page), `loadingMore` (a later page), `error` (page-level message),
 *   `className`, `studentName`, `simulations` (all loaded so far), `hasMore`, and `sentinelRef` to attach to the
 *   element rendered after the list.
 */
export function useEnrolledSimulations({ slug, onSessionExpired, onError }: UseEnrolledSimulationsArgs) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [className, setClassName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [simulations, setSimulations] = useState<SsEnrolledSimulation[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  const hasMore = page < totalPages;

  // Sign this tab out when another student redeems the same class in another tab.
  useEffect(() => {
    if (!slug) {
      return;
    }
    return onEnrolmentDisplaced(slug, () => {
      clearEnrolmentToken(slug);
      onSessionExpired();
    });
  }, [slug, onSessionExpired]);

  /**
   * Handles a failed `/enrolled` call:
   * - 401 / 403: the session is no longer valid; clear this tab's token and return to the landing page
   * - 404 / 410: clear the token and show the class-level message on the page
   * - anything else (5xx, offline): keep the session and show the API message in the alert box;
   *   when the first page fails, the page also shows a short "could not load" message
   *
   * @param err failed request (carries `userMessage` from the service)
   * @param isFirstPage whether page 1 failed (nothing on screen yet)
   */
  const handleFailure = useCallback(
    (err: any, isFirstPage: boolean) => {
      if (!slug) {
        return;
      }
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearEnrolmentToken(slug);
        onSessionExpired();
        return;
      }
      if (status === 404) {
        clearEnrolmentToken(slug);
        setError("This class landing page was not found.");
        return;
      }
      if (status === 410) {
        clearEnrolmentToken(slug);
        setError("This class is no longer accepting registrations.");
        return;
      }
      onError(err?.userMessage || "We could not load your simulations. Please try again.");
      if (isFirstPage) {
        setError("We could not load your simulations. Please refresh the page to try again.");
      }
    },
    [onError, onSessionExpired, slug]
  );

  // Load page 1 (replacing any list) whenever the slug changes. `cancelled` ignores a response
  // after unmount or a slug change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) {
        setError("Missing class link.");
        setLoading(false);
        return;
      }
      const session = getValidEnrolmentSession(slug);
      if (!session) {
        onSessionExpired();
        return;
      }
      setLoading(true);
      setError("");
      setSimulations([]);
      setPage(0);
      setTotalPages(0);
      try {
        const enrolledBody = await getEnrolled(slug, session.enrolmentToken, 1, ENROLLED_PAGE_SIZE);
        if (cancelled) {
          return;
        }
        setClassName(enrolledBody.className || "");
        setStudentName(enrolledBody.studentName || "");
        setSimulations(enrolledBody.simulations || []);
        setPage(enrolledBody.currentPage || 1);
        setTotalPages(enrolledBody.totalPages || 0);
      } catch (err: any) {
        if (!cancelled) {
          handleFailure(err, true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, onSessionExpired, handleFailure]);

  /**
   * Requests the page after the last one loaded and appends it, skipping simulations already
   * shown. A ref guard keeps it to one request at a time. On failure, loading stops and the
   * error goes to the alert box (see `handleFailure`). A 401, 403, 404, or 410 clears paging.
   * Any other failure keeps `totalPages`, so the sentinel can retry on a later scroll.
   */
  const loadNextPage = useCallback(async () => {
    if (!slug || loadingMoreRef.current) {
      return;
    }
    const session = getValidEnrolmentSession(slug);
    if (!session) {
      onSessionExpired();
      return;
    }
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const enrolledBody = await getEnrolled(slug, session.enrolmentToken, nextPage, ENROLLED_PAGE_SIZE);
      setSimulations((previous) => {
        const seen = new Set(previous.map((sim) => sim.simId));
        return [...previous, ...(enrolledBody.simulations || []).filter((sim) => !seen.has(sim.simId))];
      });
      setPage(enrolledBody.currentPage || nextPage);
      setTotalPages(enrolledBody.totalPages || 0);
    } catch (err: any) {
      const status = err?.response?.status;
      // Session or class is gone. Drop the sentinel so scroll cannot call /enrolled again.
      // A transient failure keeps totalPages so the student can scroll away and retry.
      if (status === 401 || status === 403 || status === 404 || status === 410) {
        setTotalPages(0);
      }
      handleFailure(err, false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [slug, page, onSessionExpired, handleFailure]);

  // Watch the sentinel inside the page's scroll container and load the next page when it comes
  // within 200px of view. Re-created after every page, so a list shorter than the screen keeps
  // loading until it fills the screen or runs out of pages.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!hasMore || loading || !sentinel || typeof IntersectionObserver === "undefined") {
      return;
    }
    const scrollRoot = sentinel.closest(SCROLL_ROOT_SELECTOR);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      { root: scrollRoot instanceof Element ? scrollRoot : null, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, loadNextPage]);

  return { loading, loadingMore, error, className, studentName, simulations, hasMore, sentinelRef };
}
